import {
  type AnalyticsStepCard,
  type StepCardDocuments,
} from "../../../../hooks/audit/useAnalyticsStepCards";
import { citationByDocumentId } from "../../../../lib/trailCitations";
import { type AnalyticsColumnResult } from "../../../../models/audit/analyticsColumnResult";
import {
  type AnalyticsRunPhase,
  type AnalyticsRunStep,
} from "../../../../models/audit/analyticsWorkflow";
import { type AuditorTrail } from "../../../../models/audit/attributeTestColumn";
import { type Citation } from "../../../../models/audit/citation";

/**
 * The run the verdict was judged over.
 *
 * Reached through the verdict rather than imported, because the verdict is what
 * exports its shape: the manifest is a field of it, and naming it this way is
 * one type where a second declaration would be two spellings of one record free
 * to disagree.
 */
export type VerdictRunManifest = AnalyticsColumnResult["runManifest"];

/**
 * Whether a step finished, and what a step that carries no completion time is.
 *
 * **One null, read in one direction.** A step's own record cannot say which of
 * *stopped there* and *still going* its missing timestamp means; only the run's
 * terminality settles that, so the two readings are named here and resolved in
 * one place rather than per surface.
 *
 * **A manifested run is terminal.** The manifest is written at the close of the
 * resolve stage, which only a run that has stopped reaches — so a step carrying
 * no completion in a manifested run is one that did not finish, and *still
 * running* is not a thing any surface may say about it. The direction is the
 * whole of it, and the converse is not a different rule but a false one: only a
 * run that succeeded is ever written a manifest, so a terminal run may carry
 * none at all and a missing manifest is no evidence about how a run ended.
 *
 * Without a manifest the run's terminality is not established either way: the
 * verdict carries none for a run still in flight, and carries none equally where
 * the hydration was refused. `unrecorded` reports the missing timestamp instead
 * of choosing between those two.
 */
export type StepCompletion =
  | { kind: "finished"; completedAt: string }
  | { kind: "unfinished" }
  | { kind: "unrecorded" };

/**
 * The reading of one step's completion, for every surface that shows one.
 *
 * Takes the manifest rather than a terminality flag, so the question is answered
 * here and asked nowhere else.
 */
export function stepCompletionIn(
  manifest: VerdictRunManifest,
  completedAt: string | null,
): StepCompletion {
  if (completedAt !== null) return { kind: "finished", completedAt };
  return manifest == null ? { kind: "unrecorded" } : { kind: "unfinished" };
}

/**
 * One step's completion, looked up in the verdict's own step log.
 *
 * For the surfaces that hold a step id and no timestamp — the review pane knows
 * which steps are starred and what card each has, and nothing else about when
 * they ran. The log rides the verdict those surfaces already read, so the answer
 * costs no further request.
 *
 * **It is not the read the steps tab times its rows from.** One stored log,
 * projected twice: that tab takes `completedAt` off each step of
 * `/analytics/run-steps`, and this takes it off the log hydrated onto the
 * verdict, frozen at the read that hydrated it. Two reads of one record under
 * two cache keys, free to disagree by freshness alone — so a step can carry a
 * time on one surface and none on the other, and neither is the other's
 * confirmation.
 *
 * A step the log does not carry is `unrecorded` rather than unfinished. The
 * unfinished reading is what a *logged* step with no completion means in a
 * terminal run; a step absent from the log was never reported on at all, and
 * calling it one the run stopped at would be this build naming a stopping point
 * the run never claimed.
 */
export function stepCompletionFor(
  manifest: VerdictRunManifest,
  stepId: string,
): StepCompletion {
  const entry = (manifest?.stepLog ?? []).find(
    (logged) => logged.stepId === stepId,
  );
  return entry === undefined
    ? { kind: "unrecorded" }
    : stepCompletionIn(manifest, entry.completedAt);
}

/**
 * When the run ran, as the earliest moment any of its steps is recorded
 * finishing, or null where none is.
 *
 * Neither the verdict nor `/analytics/run-steps` carries a timestamp for the run
 * itself, so the date is read off its steps. It is the date a surface heads the
 * run's steps with and the day each row's time is read against, which is why a
 * timestamp that does not parse is skipped rather than allowed to date the run.
 */
export function runDateOf(completions: StepCompletion[]): Date | null {
  let earliest: Date | null = null;
  for (const completion of completions) {
    if (completion.kind !== "finished") continue;
    const finished = new Date(completion.completedAt);
    if (Number.isNaN(finished.getTime())) continue;
    if (earliest === null || finished < earliest) earliest = finished;
  }
  return earliest;
}

/**
 * The run's date, for every surface that states one, off the verdict's own step
 * log.
 *
 * Never off `/analytics/run-steps`, even on the tab that draws its rows from it:
 * that read is keyed on the cell rather than the verdict, so once a newer run
 * reaches the cell it describes that run while the verdict still describes its
 * own, and the two tabs would date different runs.
 */
export function verdictRunDate(manifest: VerdictRunManifest): Date | null {
  return runDateOf(
    (manifest?.stepLog ?? []).map((entry) =>
      stepCompletionIn(manifest, entry.completedAt),
    ),
  );
}

/**
 * What one step did, looked up in the verdict's own step log.
 *
 * Beside `stepCompletionFor` and in its shape, because it is the same lookup
 * against the same log for the same surfaces: the review pane holds step ids and
 * nothing else about the run, and the log it already reads carries this too. A
 * second lookup style, or a prop threaded down from the host, would be a second
 * route to a fact this one already reaches.
 *
 * Null where the log does not carry the step, or carries it without an operation
 * — the engine's vocabulary is open-ended and this build models none of it, so
 * an absent operation is an unlabelled step rather than a wrong label.
 */
export function stepOperationFor(
  manifest: VerdictRunManifest,
  stepId: string,
): string | null {
  const entry = (manifest?.stepLog ?? []).find(
    (logged) => logged.stepId === stepId,
  );
  return entry?.operation ?? null;
}

/**
 * The step the run stopped at, or null where none did.
 *
 * Reads the verdict's own step log rather than the phases, because the surface
 * that needs it reads the verdict. The two are the same steps from the same
 * source, so the step named here is the step the tab draws as unfinished.
 *
 * Null wherever terminality is unestablished, which is what keeps a run still in
 * flight from being reported as one that stopped.
 */
export function stoppedAtStepName(manifest: VerdictRunManifest): string | null {
  const stopped = (manifest?.stepLog ?? []).find(
    (entry) =>
      stepCompletionIn(manifest, entry.completedAt).kind === "unfinished",
  );
  return stopped?.stepName ?? null;
}

/**
 * The run's steps as the Deterministic Steps tab lists them, assembled from the
 * three reads that each hold a part of the answer and none of which holds it
 * whole.
 *
 * The list is the workflow's own operations in run order, imports included,
 * from `/analytics/run-steps`, less the publish steps the run produced no
 * exceptions output at and which therefore have no evidence to show. The two
 * rows that describe what the run was handed and what it returned are
 * Midship's, not the workflow's: the run declares what it did, never what it
 * was handed or what its findings amounted to, so those two are composed
 * server-side and arrive on the verdict's trail. The star on an operation is a
 * third fact again — the include flag on the citation anchored to that step's
 * card.
 *
 * **Nothing here restates what a card already says.** A row carries a glyph, a
 * name, a timestamp and a star; the rule the step applied, its record counts and
 * its prose are on the card the server rendered, and a second drawing of them
 * beside it is two accounts of one step waiting to disagree.
 */

/** A row Midship composed: prose about the run rather than a report from it. */
export interface DeterministicBookend {
  key: string;
  title: string;
  /** The sub-line naming the run's bound inputs and its population. */
  detail: string | null;
}

/**
 * Whether the server rendered a card for one step.
 *
 * The question the withholding test asks, and a three-valued one where
 * `card !== null` is two-valued: `unreadable` is neither `present` nor `absent`
 * — nothing established what the run's cards are, so this step's card can be
 * called neither there nor missing, whether that is a read that did not land or
 * a verdict naming no run to have written any. Collapsed into a plain nullable,
 * every step of an unreadable run would read as a card the run never wrote.
 */
export type CardDocumentPresence = "present" | "absent" | "unreadable";

/** One operation the workflow logged. */
export interface DeterministicOperationRow {
  key: string;
  stepName: string;
  /**
   * The engine's own name for what the step did, carried as it arrived.
   *
   * It rides the row for two readers. The publish steps this tab withholds are
   * picked out by it after each row has been joined to its card — the fact that
   * withholds them is the card's absence, which a row does not have until the
   * join is made — and the row's glyph is chosen by it, which is the only place
   * it is drawn. A row carrying an operation no glyph is mapped for draws the
   * neutral one; the vocabulary is the engine's and open-ended, so this side
   * recognising a name is never a condition of showing the step.
   */
  operation: string;
  /**
   * Whether the server rendered a card for this step, which is the fact the
   * withholding test reads and is not `card !== null`.
   *
   * The two answer different questions and the row carries both. `card` is a
   * plain nullable that goes empty for every step of a run whose cards were
   * never established; `cardDocument` carries that state as `unreadable`, so the
   * withholding test acts only where a card is genuinely `absent` and never
   * where the read behind it failed.
   */
  cardDocument: CardDocumentPresence;
  /**
   * Whether the step finished, already read against the run's terminality — the
   * raw null never reaches a renderer, because a renderer has nothing to resolve
   * it against.
   */
  completion: StepCompletion;
  /**
   * The card document the viewer opens this step's evidence by, present where
   * the run wrote a card and this build resolved it. Absent is normal, not an
   * error, and on its own never a reason to withhold a row.
   */
  card: AnalyticsStepCard | null;
  /**
   * The citation anchored to this step's card document. The star is its include
   * flag, so a row with no citation has nothing to star — and it is resolved by
   * the same function the review pane's stars come from, which is what makes
   * the starred set here and that section one piece of state rather than two
   * readings that happen to agree.
   *
   * **A claim about the card document.** The include flag travels with the
   * document, so it is resolved from the document id the cards read settled and
   * a step holds its star wherever its card resolved, not from a picture a
   * client happens to hold.
   */
  citation: Citation | null;
}

export interface DeterministicStepList {
  /** The workflow's operations an auditor can see, in run order. */
  rows: DeterministicOperationRow[];
  /**
   * The composed rows describing what the run was handed, and the ones
   * describing what it returned.
   *
   * Both are lists because both take the same rule — every composed row is
   * drawn — and the composer writes one row of each, so a second of either is
   * unexpected rather than impossible.
   */
  inputs: DeterministicBookend[];
  outputs: DeterministicBookend[];
  /**
   * Every row an auditor can see, bookends included.
   *
   * The length of what is drawn rather than a count of what the workflow ran,
   * and the two differ in both directions: Midship's two rows are steps of the
   * run as it was performed even though the workflow did not log them, and a
   * publish step withheld for having no evidence to show is an operation the
   * workflow ran with no row to count.
   */
  stepCount: number;
  /**
   * How many operations the workflow logged, counted before anything was
   * withheld.
   *
   * What separates the two ways this list reaches nothing to draw. A
   * `stepCount` of zero is a run that recorded no steps only where the run
   * logged none; it is otherwise a run whose every row this build withheld,
   * which is a statement about what is on screen and not about the run. The
   * empty state distinguishes them, and cannot from `stepCount` alone.
   */
  loggedOperationCount: number;
  /**
   * Whether anything established which rows belong in this list, which is the
   * condition on the steps count and not on the panel's failure notice.
   *
   * The withholding test reads a card's absence, and a card is absent only where
   * the run's cards were established — so wherever they were not, no publish row
   * is withheld and the row count includes publish rows an auditor would not
   * see had the question been answerable. A verdict naming no run reaches that
   * state with every row correctly drawn and nothing having failed, which is
   * why the count and the notice cannot rest on one signal. `stepCount`
   * stays a sound empty-list test either way: where nothing is withheld, an
   * empty list is one the run left empty.
   */
  stepCountUnestablished: boolean;
  /** When the verdict's run ran, per `verdictRunDate`. */
  runDate: Date | null;
}

/** Where a population figure came from, so the tab shows it rather than asserts it. */
export type PopulationProvenance =
  | { kind: "step"; stepName: string }
  | { kind: "unnamedStep" }
  | { kind: "inputParameter"; paramKey: string }
  | { kind: "populationFile" }
  | { kind: "unstated" }
  /**
   * The figure is the auditor's own, typed into the override they wrote.
   *
   * Reached wherever a figure stands on the verdict over a run that resolved
   * none — a cell no run reached, where the override dialog requires one of
   * them, and a cell whose run refused to resolve a population, where it asks
   * for one just the same. Presented without its source it would read as a
   * count some run arrived at, and the run is the one thing it is not.
   */
  | { kind: "auditorOverride" }
  /**
   * No run stands behind the cell: a verdict naming neither a manifest nor an
   * id to have hydrated one, and equally a call holding no verdict at all.
   * Both say the same thing about the cell, which is what the copy states.
   *
   * Distinct from a null total under any other provenance, which draws no
   * population line: a run that resolved no figure has nothing to state. This
   * one says something on screen, because the absence is of a run rather than
   * of a figure, and a tab silent about both would read the same whether or
   * not anything ran.
   */
  | { kind: "noRun" }
  /**
   * The verdict names a run whose manifest this build could not read, so no
   * figure and no source can be had from it.
   *
   * Distinct from a null total under any other provenance, which draws no
   * population line. This one says the read failed, because silence would let
   * a failed read pass for a run that resolved nothing, and the two are
   * indistinguishable in the manifest alone because an unreadable one arrives
   * as the absent one.
   */
  | { kind: "unreadable" };

export interface PopulationStatement {
  /**
   * Null where no figure was reached — a conclusive findings-only verdict
   * resolves none legitimately, and a cell no run touched has none to resolve.
   * It stays null rather than falling back to a count of anything else on
   * screen: a row count presented as a population reports a dozen records
   * tested for a run that tested a hundred thousand.
   */
  total: number | null;
  provenance: PopulationProvenance;
  /**
   * Whether the verdict row itself carries the run's figure, which is the
   * figure the backend composes into the Inputs row. An inconclusive verdict
   * carries none even where its run resolved one, so `total` alone cannot say
   * whether that row states the population. False wherever the figure is not
   * the run's to state.
   */
  statedOnVerdict: boolean;
}

/**
 * Split a composed row back into the title and sub-line the server joined.
 *
 * The backend authors both halves and writes them as `"{title}: {detail}"`, so
 * undoing that join is the only way to give the title its own treatment without
 * this module becoming a second author of prose the backend owns.
 */
function toBookend(step: { id: string; text: string }): DeterministicBookend {
  const separator = step.text.indexOf(": ");
  if (separator === -1) {
    return { key: step.id, title: step.text, detail: null };
  }
  return {
    key: step.id,
    title: step.text.slice(0, separator),
    detail: step.text.slice(separator + 2),
  };
}

/**
 * The trail steps Midship composed, taken by the phase each one declares.
 *
 * A composed row is a statement about the run rather than a reading of a
 * document, and `phase` is what says so: the composer writes one on the two
 * rows it authors and on nothing else, so an operation row reads null. Which
 * bookend a row is comes from its phase rather than its position, because a
 * trail missing one of the two would otherwise slide the other into its place.
 *
 * Citations cannot answer this. Three classes of step carry none — the two
 * composed rows, a step whose card render failed, and a publish step that
 * produced no exceptions output and so was given no card — and only the first
 * belongs in a bookend.
 *
 * **Transitional fallback.** A trail on which no step carries a `phase` gives
 * the phase rule nothing to identify, and that rule alone would leave the tab
 * with no bookends at all. The citation reading disqualified just above is what
 * stands in for such a trail. The question is asked once of the whole trail
 * rather than once per step, so the first trail carrying any phase retires it.
 * It comes out once every trail serialises `phase`.
 *
 * **A second row for a phase is kept, and that is the rule for both.** The
 * composer writes one row per phase, so a second is unexpected either way — but
 * a composed row is the run's own prose, and dropping one would take a statement
 * about the run off the tab with nothing on screen to show it had gone. Both
 * are therefore lists and take the one rule; the alternative is one quietly
 * stricter than the other.
 *
 * **A phased row with nothing after its title is dropped.** The composer writes
 * a bare title where it has nothing to say, and drawn here that title is a
 * section heading over nothing. Dropping it takes no statement off the tab, and
 * it keeps the step count and the panel's own population line honest about
 * what is on screen. The phase-less fallback keeps every row, because there a
 * row without a sub-line is an operation's text rather than an empty bookend.
 */
function composedBookends(trail: AuditorTrail | null | undefined): {
  inputs: DeterministicBookend[];
  outputs: DeterministicBookend[];
} {
  const steps = trail?.steps ?? [];
  const phased = steps.filter((step) => step.phase != null);

  if (phased.length === 0) {
    const [first, ...rest] = steps
      .filter((step) => step.citations.length === 0)
      .map(toBookend);
    return { inputs: first === undefined ? [] : [first], outputs: rest };
  }

  return {
    inputs: phased
      .filter((step) => step.phase === "inputs")
      .map(toBookend)
      .filter((bookend) => bookend.detail !== null),
    outputs: phased
      .filter((step) => step.phase === "outputs")
      .map(toBookend)
      .filter((bookend) => bookend.detail !== null),
  };
}

/**
 * The operation names a publish step is logged under.
 *
 * One name, because one operation creates a run's outputs: a card's absence is
 * evidence that the run produced no exceptions output only for the step that
 * produces outputs at all. `publish` is the package the publish operations are
 * grouped under in the UI rather than a name any step is logged with, and
 * `publish_email` sends a notification and writes no output for a card to have
 * been rendered from. A wider set would claim a vocabulary no step uses, and a
 * step wrongly read as a publish is dropped from the tab on the strength of it.
 */
const PUBLISH_OPERATIONS = new Set(["publish_run_history"]);

/**
 * The spelling an operation name is compared under, matching the backend's.
 *
 * `publish-run-history` and `publish_run_history` are one operation, and a name
 * folded on one side of the wire and taken raw on the other is two readings of
 * one step.
 *
 * Exported because the glyph a row draws is chosen by the same name the
 * withholding test reads, and a map keyed on one folding beside a test using
 * another is one operation spelled two ways.
 */
export function normalisedOperation(operation: string): string {
  return operation.trim().toLowerCase().replace(/-/g, "_");
}

/**
 * A publish step the tab does not list.
 *
 * A publish step that produced no `exceptions`-roled output is given no card, and
 * there is nothing for such a row to open: no rule, no counts and no prose, only
 * a name and a time under a heading that offers evidence.
 *
 * **The card's absence is the whole signal, and absence means the server wrote
 * none.** `absent` is the read-state answer that this run rendered no card for
 * the step, and it is distinct from `unreadable`, where nothing established the
 * run's cards at all — so a failed read withholds no row. The row is drawn with
 * no picture in it, which is what the viewer's own empty state is for.
 *
 * **This is every way a row vanishes, and two of them are not the backend's
 * decision.** A publish step whose card render failed server-side is withheld
 * here, and so is one this build cannot join to a card at all — a step arriving
 * with no usable id. What was in a position to place it is a fact about the
 * payload rather than about the row: the positional fallback is asked for only
 * where no step in the payload carries an id, so on a payload where any step
 * does, an id-less step is left unplaced with no fallback attempted for it at
 * all. Both read as a card the run never wrote. The first is the conflation nothing can
 * resolve: the render failure is settled while the run's evidence is drawn and
 * is written down nowhere a client can read, so no stored fact tells it apart
 * from a deliberate withholding. The second is this build's join reaching its
 * limit: the row carries no step id to have matched, which is legible in the
 * data and nowhere on screen — the row is simply gone, with nothing in its
 * place to say a step was dropped.
 *
 * Every other absence is reported instead of acted on: wherever the run's cards
 * were never established — a read that did not land, a log those cards could
 * not be matched against, a verdict naming no run to have written any — no
 * step's card is called missing and nothing is withheld.
 */
function isWithheldPublishStep(row: DeterministicOperationRow): boolean {
  return (
    row.cardDocument === "absent" &&
    PUBLISH_OPERATIONS.has(normalisedOperation(row.operation))
  );
}

/**
 * The id a run step carries, where it carries a usable one.
 *
 * An empty id is absent rather than a key, because blank is an identity no step
 * actually has: joined on, it would collect every step carrying it onto one
 * card, which is the mis-attribution the id join exists to end. The test costs
 * nothing to hold whatever the wire sends, and being wrong about what it sends
 * costs a card opened under another step's name.
 */
function joinableStepId(step: AnalyticsRunStep): string | null {
  return step.stepId === null || step.stepId === "" ? null : step.stepId;
}

/**
 * The card document one step has, where this build can name both the step and
 * the run's cards.
 *
 * Null covers a step the run wrote no card for and a step nothing here can
 * answer for alike; `cardDocumentPresence` is what separates the two.
 *
 * Answered arm by arm rather than by testing for `read`, so a state added to
 * the union has to say here what a row may open under it instead of arriving
 * as one more thing that is not `read`.
 */
function cardDocumentIdFor(
  stepId: string | null,
  cardDocuments: StepCardDocuments,
): string | null {
  if (stepId === null) return null;
  switch (cardDocuments.kind) {
    case "read":
      return cardDocuments.cardDocumentIdByStepId.get(stepId) ?? null;
    case "noRun":
    case "unreadable":
      return null;
    default: {
      const unhandled: never = cardDocuments;
      return unhandled;
    }
  }
}

/**
 * Whether one step has a card, answered from the cards read's state rather than
 * from whether a card object resolved.
 *
 * **A card is called missing only where the run's cards were established.**
 * `read` is the one state that establishes them, and it is the resolver that
 * says so: it walks the run's own step log to name the steps a card is matched
 * against, and reports every way that could not be done instead of handing back
 * the empty set each of them leaves. An empty answer from any other state is
 * the absence of any way to ask, which is the one thing `absent` must never
 * mean — so this reads the state and never a second projection of the reads
 * behind it, which would be free to disagree with the resolver's own reading.
 *
 * A step this build could not name is `absent` rather than `unreadable`: the
 * join is what failed, not the read, and a run's cards are established just as
 * well for every step beside it. That keeps the unreadable state the one thing
 * it says — the cards were never established — and leaves the id join's own
 * limit to the withholding test, which names it.
 *
 * Every arm answers for itself, so a state added to the union cannot take
 * `absent` by default. This is the answer a row is withheld on, and withholding
 * a publish row states that the run gave it no evidence.
 */
function cardDocumentPresence(
  cardDocumentId: string | null,
  cardDocuments: StepCardDocuments,
): CardDocumentPresence {
  switch (cardDocuments.kind) {
    case "read":
      return cardDocumentId === null ? "absent" : "present";
    case "noRun":
    case "unreadable":
      return "unreadable";
    default: {
      const unhandled: never = cardDocuments;
      return unhandled;
    }
  }
}

/**
 * Compose the list the tab renders.
 *
 * A row is joined to its card by the id the run's own step payload carries.
 * Identity is the join because nothing else about a step is unique: two adjacent
 * steps sharing a name are indistinguishable to a positional read however
 * carefully it checks names, and a card opened under the wrong step's name is
 * worse than a step shown with no card at all.
 *
 * **Withheld rows come out of the result and never out of the input.** The
 * fallback join below reads `manifest.stepLog`, which is served on another
 * endpoint and filtered by nothing, so a list shortened before the join would
 * slide every row after the cut onto another step's card. Rows are built from
 * the whole payload, and the withheld ones are dropped from what is returned.
 *
 * The payload's phases are flattened into one list in run order; the tab draws
 * no phase headings, so nothing of a phase survives the flattening.
 */
export function composeDeterministicSteps({
  phases,
  manifest,
  trail,
  cards,
  cardDocuments,
}: {
  phases: AnalyticsRunPhase[];
  manifest: VerdictRunManifest;
  trail: AuditorTrail | null | undefined;
  /** The resolved cards, each carrying the document a selected row opens. */
  cards: AnalyticsStepCard[];
  /** Which steps have a card at all, which is what decides a row's fate. */
  cardDocuments: StepCardDocuments;
}): DeterministicStepList {
  const stepLog = manifest?.stepLog ?? [];
  const cardByStepId = new Map(cards.map((card) => [card.stepId, card]));
  const citations = citationByDocumentId(trail);

  const logged = phases.flatMap((phase) => phase.steps);

  // **Transitional fallback.** A payload carrying no step id at all is matched to
  // the verdict's step log by position — against the run's own flattened order,
  // which is what the log records and which does not restart at each phase —
  // with the names checked as the join is made. It is the weaker join, since two
  // adjacent steps sharing a name pass that check misaligned. The question is
  // asked once of the whole payload rather than once per step, so the first
  // payload carrying any id retires it, and it comes out once every deployment
  // sends `stepId`.
  const joinsById = logged.some((step) => joinableStepId(step) !== null);
  const stepIdFor = (step: AnalyticsRunStep, index: number): string | null => {
    if (joinsById) {
      return joinableStepId(step);
    }
    const entry = stepLog[index];
    return entry !== undefined && entry.stepName === step.name
      ? entry.stepId
      : null;
  };

  const rows = logged
    .map((step, index): DeterministicOperationRow => {
      const stepId = stepIdFor(step, index);
      const cardDocumentId = cardDocumentIdFor(stepId, cardDocuments);
      const card =
        (stepId === null ? undefined : cardByStepId.get(stepId)) ?? null;
      return {
        key: stepId ?? `step-${index}`,
        stepName: step.name,
        operation: step.operation,
        completion: stepCompletionIn(manifest, step.completedAt),
        cardDocument: cardDocumentPresence(cardDocumentId, cardDocuments),
        card,
        citation:
          cardDocumentId === null
            ? null
            : (citations.get(cardDocumentId) ?? null),
      };
    })
    .filter((row) => !isWithheldPublishStep(row));

  const bookends = composedBookends(trail);

  return {
    rows,
    inputs: bookends.inputs,
    outputs: bookends.outputs,
    stepCount: rows.length + bookends.inputs.length + bookends.outputs.length,
    loggedOperationCount: logged.length,
    stepCountUnestablished: cardDocuments.kind !== "read",
    runDate: verdictRunDate(manifest),
  };
}

/**
 * The population and where it came from.
 *
 * Takes the verdict rather than its manifest, because two of the answers are
 * not in the manifest. A manifest this build cannot parse arrives as no
 * manifest at all, and only the verdict's own `runManifestId` says a run was
 * there to report. And the figure itself rides the verdict as well as the
 * manifest: wherever a run resolved no denominator the override asks the
 * auditor for one and stores it on the row, so reading the manifest alone
 * would compel a number, keep it, and report that none was resolved.
 *
 * Provenance is resolved from the field that carries it rather than from the
 * source name, because the run's sources do not answer through one field: a
 * figure inferred from an input the classifier nominated names a parameter and
 * leaves the step id null, while a figure read from a published file names no
 * field and is known by its source alone. Reading the step id alone would
 * render nothing for the inferred figure, which is the source most in need of
 * explaining.
 *
 * The backend never writes `populationStepId` and never resolves `import_step`
 * as a source — a run resolves either a published population file or a
 * classifier-nominated input, so the step id always arrives null. The manifest
 * keeps the column and the CHECK tying its presence to an `import_step` source
 * against a future where per-step counts arrive, so the `step` and `unnamedStep`
 * arms below guard a shape no run reaches today. They are kept because a figure
 * whose origin is unrecorded is one nobody can audit: were such a run to arrive,
 * a counting step this build cannot resolve to a name would read as `unnamedStep`
 * and not `unstated`, so a sourced figure is not presented as a bare one.
 */
export function resolvePopulation(
  verdict: Pick<
    AnalyticsColumnResult,
    "runManifest" | "runManifestId" | "populationTotal"
  > | null,
): PopulationStatement {
  const manifest = verdict?.runManifest ?? null;

  // A verdict that names a run but carries no manifest for it: the manifest is
  // parsed leniently, so one whose shape has moved past this build arrives as
  // the absent one. `runManifestId` is what separates them, because it rides
  // the row itself rather than the hydration.
  if (manifest === null && verdict?.runManifestId != null) {
    return {
      total: null,
      provenance: { kind: "unreadable" },
      statedOnVerdict: false,
    };
  }

  // A figure on the row standing over a run that resolved none is the
  // auditor's, and the two halves of that test are each necessary. An override
  // carries its predecessor's manifest forward, so naming a run does not make
  // the figure the run's; and a supplied figure contradicting one already
  // resolved — by the row or by the manifest — is refused rather than stored,
  // so a run that did resolve one leaves no room for a second author.
  const supplied = verdict?.populationTotal ?? null;
  if (supplied !== null && manifest?.populationTotal == null) {
    return {
      total: supplied,
      provenance: { kind: "auditorOverride" },
      statedOnVerdict: false,
    };
  }

  if (manifest === null) {
    return {
      total: null,
      provenance: { kind: "noRun" },
      statedOnVerdict: false,
    };
  }

  // The row's copy first, which the run's own figure is read into. They cannot
  // disagree — the route refuses a figure that contradicts either — so this is
  // one figure reached by whichever field carries it.
  const total = supplied ?? manifest.populationTotal;
  const statedOnVerdict = supplied !== null;

  const stepName = (manifest.stepLog ?? []).find(
    (entry) => entry.stepId === manifest.populationStepId,
  )?.stepName;
  if (stepName !== undefined) {
    return {
      total,
      provenance: { kind: "step", stepName },
      statedOnVerdict,
    };
  }

  const paramKey = manifest.populationInputParamKey;
  if (paramKey) {
    return {
      total,
      provenance: { kind: "inputParameter", paramKey },
      statedOnVerdict,
    };
  }

  if (manifest.populationSource === "population_file") {
    return {
      total,
      provenance: { kind: "populationFile" },
      statedOnVerdict,
    };
  }

  if (
    manifest.populationStepId != null ||
    manifest.populationSource === "import_step"
  ) {
    return { total, provenance: { kind: "unnamedStep" }, statedOnVerdict };
  }

  return { total, provenance: { kind: "unstated" }, statedOnVerdict };
}
